import { ICDCorporationListRM, IcdCorporationResponse, PayrollAnalysisSummary, RSScheduleResponseModel } from '@herbjoy/apex-api-models';
import angular, { IOnInit, IPromise, IQService } from 'angular';
import { StateService } from 'angular-ui-router';
import moment from 'moment';
import { PayrollService } from '../../../../core/services';
import { UtilityService } from '../../../../core/services/utility.service';
import templateUrl from './payroll-analysis-summary.component.html';


interface PayWeek {
    StartDate: Date;
    EndDate: Date;
    Text: string;
    DateSpan: string;
}

interface PayrollAnaysisSummaryExtension extends PayrollAnalysisSummary {
    detailHref?: string;
    icdCorporation?: IcdCorporationResponse;
}

export interface ProgramSelect {
    Program: string;
    ProgramID: number;
}

export interface RetailerSelect {
    Retailer: string;
    RetailerID: number;
}

interface OwnerSelect {
    Text: string;
    Value: string;
}

export interface PayrollAnalysisAggregateFields {
    AdminGrossPay?: any;
    Bonus?: any;
    CellAllowance?: any;
    Tip?: any;
    CommissionBonus?: any;
    CommissionIncome?: any;
    CommissionIncomeRate?: any;
    ContractorGrossPay?: any;
    DoubleTimeHours?: any;
    DoubleTimePremium?: any;
    ExtraCommissionPaid?: any;
    GeneralHours?: any;
    GeneralHoursPercent?: any;
    GrossPay?: any;
    HolidayPay?: any;
    MinimumWageTopUp?: any;
    MinimumWageTopUpPercent?: any;
    NetPayAddition?: any;
    NetPayDeduction?: any;
    OvertimeHours?: any;
    OvertimePremium?: any;
    OwnerGrossPay?: any;
    PaidHours?: any;
    RegularCommissionPaid?: any;
    RegularHours?: any;
    RepGrossPayPercent?: any;
    RepGrossPayRate?: any;
    Salary?: any;
    ShowCell?: any;
    ShowTip?: any;
    ShowDT?: any;
    ShowHolidayPay?: any;
    ShowSick?: any;
    ShowSplit?: any;
    ShowTravel?: any;
    SickPayAmount?: any;
    SplitTimeAmount?: any;
    TotalCommissionAmount?: any;
    TotalGuaranteed?: any;
    TotalHours?: any;
    TravelAllowance?: any;
    UnproductiveHours?: any;
}

interface PayrollAnalysisSummaryReport extends PayrollAnalysisAggregateFields {
    payrolls: PayrollAnaysisSummaryExtension[];
    average: any;
}

export const PayrollAnalysisSummaryComponent = {
    bindings: {
        startDate: "<",
        endDate: "<",
        programID: "<",
        retailerID: "<",
        owner: "<"
    },
    templateUrl,
    controller: class PayrollAnalysisSummaryComponent implements IOnInit {
        //@Input
        startDate: Date;
        //@Input
        endDate: Date;
        //@Input
        programID: number;
        //@Input
        retailerID: number;
        //@Input
        owner: string;
        filter: { program?: ProgramSelect, retailer?: RetailerSelect } = {};
        getIcdCorporations: IPromise<ICDCorporationListRM[]>
        ownerModel: OwnerSelect;
        owners = new kendo.data.ObservableArray([]) as any as OwnerSelect[];
        payrollAnalysisSummary: PayrollAnalysisSummaryReport;
        payWeek: PayWeek;
        payWeekList = new kendo.data.ObservableArray([]) as any as PayWeek[];
        program: ProgramSelect;
        programs = new kendo.data.ObservableArray([]) as any as ProgramSelect[];
        retailer: RetailerSelect;
        retailers = new kendo.data.ObservableArray([]) as any as RetailerSelect[];
        rsSchedules = new kendo.data.ObservableArray([]) as any as RSScheduleResponseModel[];
        showFilters = true;
        constructor(private $q: IQService, private $state: StateService, private icdCorporationService,
            private payrollService: PayrollService, private rsScheduleService,
            private utilityService: UtilityService, private apexApiUrl: string) {
            'ngInject';
        }

        $onInit() {
            this.utilityService.showLoading();

            // Will not be used directly, but resolve it in other async loaders when needed.
            this.getIcdCorporations = this.icdCorporationService.retrieveIcdCorporations();

            this.loadPayWeeks()
                .then(payWeeks => {
                    this.payWeek = payWeeks.filter(payWeek => {
                        return moment(payWeek.StartDate).isSame(this.startDate, "day")
                            && moment(payWeek.EndDate).isSame(this.endDate, "day");
                    })[0] || payWeeks[0];
                    // Turn start and end dates from strings into dates.
                    // Angular keeps reassigning these to their original values within the
                    // constructor (???), so reassign them here instead.
                    if (this.payWeek) {
                        this.startDate = this.payWeek.StartDate;
                        this.endDate = this.payWeek.EndDate;
                    }
                    return this.payWeek;
                })
                .then(payWeek => this.loadRSSchedulesForPeriod(payWeek))
                .catch(ex => {
                    console.error(ex);
                    alert("Initial load failed.");
                })
                .then(() => this.utilityService.hideLoading());
        }

        onClickGenerateSummary() {
            this.utilityService.showLoading();
            this.loadPayrollAnalysisSummary()
                .then(() => this.changePageState())
                .then(() => {
                    // Auto-collapse the filters.
                    this.showFilters = false;
                })
                .catch(ex => {
                    console.error(ex);
                    alert("Generating summary failed: " + (ex.message !== undefined ? ex.message
                        : ex.data ? ex.data.Message
                            : ex.statusText !== undefined ? ex.statusText
                                : ex));
                })
                .then(() => this.utilityService.hideLoading());
        }

        onClickExportSummary() {
            window.location.href = this.apexApiUrl + "/payrollreports/payroll-analysis-summary" + this.utilityService.queryStringify({
                startDate: this.startDate ? kendo.format("{0:yyyy-MM-dd}", this.startDate) : null,
                endDate: this.endDate ? kendo.format("{0:yyyy-MM-dd}", this.endDate) : null,
                programID: this.programID,
                retailerID: this.retailerID,
                owner: this.owner
            });
        }

        onChangePayWeek() {
            this.utilityService.showLoading();
            this.changePayWeek(this.payWeek)
                .catch(angular.noop)
                .then(() => this.utilityService.hideLoading());
        }

        onChangeProgram() {
            this.programID = this.filter.program ? this.filter.program.ProgramID : null;
        }

        onChangeRetailer() {
            this.retailerID = this.filter.retailer ? this.filter.retailer.RetailerID : null;
        }

        onChangeOwner() {
            this.owner = this.ownerModel ? this.ownerModel.Value : null;
        }

        loadPayrollAnalysisSummary() {
            return this.payrollService.retrievePayrollAnalysisSummary(this.payWeek ? this.payWeek.StartDate : null,
                this.payWeek ? this.payWeek.EndDate : null, this.programID, this.retailerID, this.owner)
                .then(summary => {
                    this.payrollAnalysisSummary = {
                        payrolls: summary.sort((a, b) => {
                            return a.StartDate < b.StartDate
                                ? -1
                                : a.StartDate > b.StartDate
                                    ? 1
                                    : a.Owner < b.Owner
                                        ? -1
                                        : 1;
                        }),
                        average: {}
                    };

                    this.payrollAnalysisSummary.payrolls.reduce((summary, payroll) => {
                        // Create aggregate totals for the summary.
                        summary.CommissionIncome = (summary.CommissionIncome || 0) + payroll.CommissionIncome;
                        // Adding up these rates won't make sense on their own, but they are used later to compute averages.
                        summary.CommissionIncomeRate = (summary.CommissionIncomeRate || 0)
                            + payroll.CommissionIncomeRate;
                        summary.RegularCommissionPaid = (summary.RegularCommissionPaid || 0)
                            + payroll.RegularCommissionPaid;
                        summary.ExtraCommissionPaid = (summary.ExtraCommissionPaid || 0) + payroll.ExtraCommissionPaid;
                        summary.MinimumWageTopUp = (summary.MinimumWageTopUp || 0) + payroll.MinimumWageTopUp;
                        summary.MinimumWageTopUpPercent = (summary.MinimumWageTopUpPercent || 0)
                            + payroll.MinimumWageTopUpPercent;
                        summary.GeneralHours = (summary.GeneralHours || 0) + payroll.GeneralHours;
                        summary.GeneralHoursPercent = (summary.GeneralHoursPercent || 0) + payroll.GeneralHoursPercent;
                        summary.UnproductiveHours = (summary.UnproductiveHours || 0) + payroll.UnproductiveHours;
                        summary.PaidHours = (summary.PaidHours || 0) + payroll.PaidHours;
                        summary.TotalCommissionAmount = (summary.TotalCommissionAmount || 0)
                            + payroll.TotalCommissionAmount;
                        summary.OvertimePremium = (summary.OvertimePremium || 0) + payroll.OvertimePremium;
                        summary.DoubleTimePremium = (summary.DoubleTimePremium || 0) + payroll.DoubleTimePremium;
                        summary.Bonus = (summary.Bonus || 0) + payroll.Bonus;
                        summary.CommissionBonus = (summary.CommissionBonus || 0) + (payroll.CommissionBonus || 0);
                        summary.TravelAllowance = (summary.TravelAllowance || 0) + payroll.TravelAllowance;
                        summary.CellAllowance = (summary.CellAllowance || 0) + payroll.CellAllowance;
                        summary.Tip = (summary.Tip || 0) + payroll.Tip;
                        summary.SplitTimeAmount = (summary.SplitTimeAmount || 0) + payroll.SplitTimeAmount;
                        summary.SickPayAmount = (summary.SickPayAmount || 0) + payroll.SickPayAmount;
                        summary.HolidayPay = (summary.HolidayPay || 0) + payroll.HolidayPay;
                        summary.RepGrossPayPercent = (summary.RepGrossPayPercent || 0) + payroll.RepGrossPayPercent;
                        summary.RepGrossPayRate = (summary.RepGrossPayRate || 0) + payroll.RepGrossPayRate;
                        summary.OwnerGrossPay = (summary.OwnerGrossPay || 0) + payroll.OwnerGrossPay;
                        summary.AdminGrossPay = (summary.AdminGrossPay || 0) + payroll.AdminGrossPay;
                        summary.ContractorGrossPay = (summary.ContractorGrossPay || 0) + payroll.ContractorGrossPay;
                        summary.GrossPay = (summary.GrossPay || 0) + payroll.GrossPay;

                        // Show columns if any of the payrolls show the column.
                        summary.ShowDT = summary.ShowDT || payroll.ShowDT;
                        summary.ShowTravel = summary.ShowTravel || payroll.ShowTravel;
                        summary.ShowCell = summary.ShowCell || payroll.ShowCell;
                        summary.ShowTip = summary.ShowTip || payroll.ShowTip;
                        summary.ShowSplit = summary.ShowSplit || payroll.ShowSplit;
                        summary.ShowSick = summary.ShowSick || payroll.ShowSick;
                        summary.ShowHolidayPay = summary.ShowHolidayPay || payroll.ShowHolidayPay;

                        const args = {
                            programID: this.programID,
                            retailerID: this.retailerID
                        };
                        payroll.detailHref = "Report/PayrollAnalysis/" + payroll.PayrollID + this.utilityService.queryStringify(args);
                        return summary;
                    }, this.payrollAnalysisSummary);

                    // Compute averages
                    const len = this.payrollAnalysisSummary.payrolls.length || 1;
                    const average = this.payrollAnalysisSummary.average;
                    const sum = this.payrollAnalysisSummary;
                    average.GeneralHours = sum.GeneralHours / len;
                    average.GeneralHoursPercent = sum.GeneralHoursPercent / len;
                    average.CommissionIncomeRate = sum.CommissionIncomeRate / len;
                    average.PaidHours = sum.PaidHours / len;
                    average.CommissionIncomeRate = sum.CommissionIncomeRate / len;
                    average.MinimumWageTopUp = sum.MinimumWageTopUp / len;
                    average.MinimumWageTopUpPercent = sum.MinimumWageTopUpPercent / len;
                    average.RepGrossPayPercent = sum.RepGrossPayPercent / len;
                    average.RepGrossPayRate = sum.RepGrossPayRate / len;

                    this.program = this.filter.program;
                    this.retailer = this.filter.retailer;

                    return this.getIcdCorporations.then(icdCorporations => {
                        // Load data about the corporation into the summary.
                        this.payrollAnalysisSummary.payrolls.forEach(payroll => {
                            payroll.icdCorporation = icdCorporations.filter(icdCorporation => {
                                return icdCorporation.Id === payroll.IcdCorporationID;
                            })[0];
                        });

                        return this.payrollAnalysisSummary;
                    });
                });
        }

        loadPayWeeks(): IPromise<PayWeek[]> {
            return this.payrollService.retrievePayrolls({})
                .then(payrolls => {
                    return payrolls.filter(payroll => {
                        return payroll.Locked;
                    });
                })
                .then(payrolls => this.reducePayrollsToPayWeeks(payrolls))
                .then(payWeeks => {
                    return payWeeks.sort((a, b) => {
                        return moment(a.StartDate).isBefore(b.StartDate) ? 1 : -1;
                    });
                })
                .then(payWeeks => {
                    payWeeks.forEach(payWeek => {
                        payWeek.Text = kendo.format("{0:MM/dd/yyyy} - {1:MM/dd/yyyy}", payWeek.StartDate, payWeek.EndDate);
                        payWeek.DateSpan = kendo.format("{0:MMM dd, yyyy} - {1:MMM dd, yyyy}", payWeek.StartDate, payWeek.EndDate);
                    });
                    this.payWeekList.push(...payWeeks);
                    return this.payWeekList;
                });
        }

        reducePayrollsToPayWeeks(payrolls): PayWeek[] {
            return Object.values(payrolls.reduce((payWeeks, payroll) => {
                const isNew = !payWeeks.filter(payWeek => {
                    return moment(payWeek.StartDate).isSame(payroll.StartDate, 'day')
                        && moment(payWeek.EndDate).isSame(payroll.EndDate, 'day');
                }).length;

                if (isNew) {
                    const payWeek = {
                        PayWeekID: payroll.PayWeekID,
                        StartDate: payroll.StartDate,
                        EndDate: payroll.EndDate
                    };
                    payWeeks.push(payWeek);
                }
                return payWeeks;
            }, []));
        }

        loadRSSchedulesForPeriod(payWeek: PayWeek) {
            this.rsSchedules.splice(0, this.rsSchedules.length);
            this.programs.splice(0, this.programs.length);
            this.retailers.splice(0, this.retailers.length);
            this.owners.splice(0, this.owners.length);
            if (!payWeek) {
                return this.$q.resolve(this.rsSchedules);
            }

            const query = {
                StartDate: payWeek.StartDate,
                EndDate: payWeek.EndDate
            }

            return this.rsScheduleService.retrieveRSSchedules(query)
                .then(rsSchedules => {
                    this.rsSchedules.push(...rsSchedules);

                    // Get distinct lists of programs and retailers from the schedule.
                    const programs = rsSchedules.reduce(function (setList, program) {
                        if (!setList.set[program.ProgramID]) {
                            setList.set[program.ProgramID] = true;
                            setList.list.push(program);
                        }
                        return setList;
                    }, { set: {}, list: [] }).list.sort(function (a, b) {
                        return a.Program < b.Program ? -1 : 1;
                    });
                    this.programs.push(...programs);
                    this.filter.program = this.programs.filter(program => {
                        return program.ProgramID === this.programID;
                    })[0];

                    const retailers = rsSchedules.reduce(function (setList, retailer) {
                        if (!setList.set[retailer.RetailerID]) {
                            setList.set[retailer.RetailerID] = true;
                            setList.list.push(retailer);
                        }
                        return setList;
                    }, { set: {}, list: [] }).list.sort(function (a, b) {
                        return a.Retailer < b.Retailer ? -1 : 1;
                    });
                    this.retailers.push(...retailers);
                    this.filter.retailer = this.retailers.filter(retailer => {
                        return retailer.RetailerID === this.retailerID;
                    })[0];

                    // It isn't necessary that the corporations list be loaded until this point.
                    return this.getIcdCorporations.then(icdCorporations => {
                        const owners = rsSchedules.reduce((setList, rsSchedule) => {
                            const owner = icdCorporations.filter(icdCorporation => {
                                return icdCorporation.Id === rsSchedule.IcdCorporationID;
                            })[0].Owner;
                            if (!setList.set[owner]) {
                                setList.set[owner] = true;
                                setList.list.push({
                                    Text: owner,
                                    Value: owner
                                });
                            }
                            return setList;
                        }, { set: {}, list: [] }).list.sort(function (a, b) {
                            return a.Text < b.Text ? -1 : 1;
                        });
                        this.owners.push(...owners);
                        this.ownerModel = this.owners.filter(owner => {
                            return owner.Text === this.owner;
                        })[0];
                    });
                });
        }

        changePayWeek(payWeek: PayWeek) {
            this.startDate = payWeek.StartDate;
            this.endDate = payWeek.EndDate;
            return this.loadRSSchedulesForPeriod(payWeek);
        }

        changePageState() {
            const args = {
                startDate: this.startDate ? kendo.format("{0:yyyy-MM-dd}", this.startDate) : null,
                endDate: this.endDate ? kendo.format("{0:yyyy-MM-dd}", this.endDate) : null,
                programID: this.programID,
                retailerID: this.retailerID,
                owner: this.owner
            };

            // Change the arguments to the current page.
            this.$state.go(".", args);
        }


        createQueryString(args) {
            return Object.keys(args).reduce(function (queryString, arg) {
                const val = args[arg];
                return queryString + (val ? (queryString ? "&" : "?") + arg + "=" + val : "");
            }, "");
        }

        showOnlyFilteredColumns() {
            return !(this.program && this.program.ProgramID)
                && !(this.retailer && this.retailer.RetailerID);
        }
    },
    controllerAs: "vm"
};
